"use client";
import React, { useState } from "react";
import { Globe } from "lucide-react";
import { motion, AnimatePresence } from "motion/react";
import { useLanguageContext } from "../context/LanguageContext";
import { Button } from "./ui/button";

export const LanguageSwitcher: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { language, setLanguage } = useLanguageContext();

  const languages = [
    { code: "en", label: "English", flag: "🇬🇧" },
    { code: "ar", label: "العربية", flag: "🇸🇦" },
    { code: "tr", label: "Türkçe", flag: "🇹🇷" },
  ] as const;

  const currentLanguage = languages.find((lang) => lang.code === language);

  return (
    <div className="relative">
      <Button
        variant="glass"
        pill
        onClick={() => setIsOpen(!isOpen)}
        className="h-[36px] px-4 relative"
        aria-label="Change language"
      >
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none rounded-[inherit] glass-shadow-inset"
        />
        <Globe className="w-4 h-4 relative z-10 text-grey-800 dark:text-grey-200" />
        <span className="text-sm font-medium relative z-10 text-grey-800 dark:text-grey-200">
          {currentLanguage?.flag}
        </span>
      </Button>

      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop */}
            <div
              className="fixed inset-0 z-40"
              onClick={() => setIsOpen(false)}
            />

            {/* Dropdown */}
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
              className="absolute top-12 right-0 z-50 backdrop-blur-[20px] bg-white/90 dark:bg-grey-900/90 rounded-[20px] border border-white/20 dark:border-white/10 glass-shadow-outer overflow-hidden min-w-[160px]"
            >
              <div className="p-2">
                {languages.map((lang) => (
                  <Button
                    key={lang.code}
                    variant="ghost"
                    onClick={() => {
                      setIsOpen(false);
                      setLanguage(lang.code);
                    }}
                    className={`w-full px-4 py-2.5 rounded-[15px] text-sm font-medium justify-start gap-3 ${
                      language === lang.code
                        ? "bg-walnut-500/20 text-walnut-900 dark:text-white"
                        : "text-grey-700 dark:text-grey-300 hover:bg-white/50 dark:hover:bg-white/10"
                    }`}
                  >
                    <span className="text-lg">{lang.flag}</span>
                    <span>{lang.label}</span>
                  </Button>
                ))}
              </div>
              <div className="absolute inset-0 pointer-events-none rounded-[inherit] glass-shadow-inset" />
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};
